import React, { useState, useEffect } from 'react';
import {
  Form,
  Alert,
  Spinner,
  InputGroup
} from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { useGlobal } from '../../../Context/GlobalContext';
import { useWorkspace } from '../../../Context/WorkspaceContext';
import { FiMic } from 'react-icons/fi';
import { useFeaturePermissions } from '../../../hooks/useWorkspacePermissions';
import { RateLimitingSettingsSkeleton } from '../../../Components/SkeletonLoaders';
import PageHeader from '../../../Components/PageHeader';
import '../agent-settings.css';
import { useLanguage } from '../../../Context/LanguageContext';

const VoiceRateLimitingSettings = () => {
  const { id: chatbotId } = useParams();
  const { apiUrl } = useGlobal();
  const { fetchWorkspaceDetails, currentWorkspace } = useWorkspace();
  const { t } = useLanguage();

  // Get permission data
  const { agentSettings: settingsPermissions } = useFeaturePermissions();

  // Default values for Voice Rate Limiting settings
  const defaultVoiceRateLimiting = {
    turnsPerMinute: 15,
    turnsPerHour: 40,
    turnsPerDay: 80,
    maxConversationDuration: 30,
    burstLimit: 4,
    blockDuration: 60,
    maxConcurrentSessions: 1,
  };

  // Settings state
  const [voiceRateLimitSettings, setVoiceRateLimitSettings] = useState(defaultVoiceRateLimiting);
  const [lastSavedVoiceRateLimit, setLastSavedVoiceRateLimit] = useState(defaultVoiceRateLimiting);

  // Save and error state
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(true);

  // Function to fetch voice rate limiting settings
  const fetchVoiceRateLimitSettings = async () => {
    setLoading(true);
    try {
      const brandvoxToken = localStorage.getItem('brandvox_token');
      const response = await fetch(
        `${apiUrl}/api/v1/chatbot/get-chatbot-general-settings`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${brandvoxToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ chatbot_id: chatbotId })
        }
      );
      if (!response.ok) throw new Error(t('dashboard.settings.voiceratelimitingsettings.errorFetchSettings'));
      const data = await response.json();

      // Voice rate limit settings are included in the general settings response
      const voiceRateLimitData = data?.voice_rate_limit_settings || {};
      const fetchedVoiceRateLimit = {
        ...defaultVoiceRateLimiting,
        ...voiceRateLimitData,
      };
      setVoiceRateLimitSettings(fetchedVoiceRateLimit);
      setLastSavedVoiceRateLimit(fetchedVoiceRateLimit);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Initial loading: fetch voice rate limiting settings
  useEffect(() => {
    fetchVoiceRateLimitSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, chatbotId]);

  // Helper function to check if settings have changed
  const isVoiceRateLimitChanged = () =>
    JSON.stringify(voiceRateLimitSettings) !== JSON.stringify(lastSavedVoiceRateLimit);

  // Helper function to check if there are validation errors
  const hasValidationErrors = () => Object.keys(fieldErrors).length > 0;

  // Save function for Voice Rate Limiting Settings
  const handleSaveVoiceRateLimit = async () => {
    setIsSaving(true);
    setError(null);
    setFieldErrors({});

    // Validate settings before sending to API
    const validationErrors = validateAllVoiceRateLimitSettings(voiceRateLimitSettings);
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      setError(t('dashboard.settings.voiceratelimitingsettings.validationErrorCorrect'));
      setIsSaving(false);
      return;
    }

    try {
      const brandvoxToken = localStorage.getItem('brandvox_token');

      // Prepare request body according to schema
      const requestBody = {
        chatbot_id: chatbotId,
        turnsPerMinute: voiceRateLimitSettings.turnsPerMinute,
        turnsPerHour: voiceRateLimitSettings.turnsPerHour,
        turnsPerDay: voiceRateLimitSettings.turnsPerDay,
        maxConversationDuration: voiceRateLimitSettings.maxConversationDuration,
        burstLimit: voiceRateLimitSettings.burstLimit,
        blockDuration: voiceRateLimitSettings.blockDuration,
        maxConcurrentSessions: voiceRateLimitSettings.maxConcurrentSessions,
      };

      const response = await fetch(`${apiUrl}/api/v1/chatbot/update-voice-rate-limit-settings`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${brandvoxToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const data = await response.json();

        // Handle different error response formats
        if (data.detail && typeof data.detail === 'object') {
          // Backend returned structured error with field_errors
          if (data.detail.field_errors) {
            setFieldErrors(data.detail.field_errors);
            setError(data.detail.message || t('dashboard.settings.voiceratelimitingsettings.validationErrorHighlight'));
          } else {
            setError(data.detail.message || t('dashboard.settings.voiceratelimitingsettings.errorUpdateSettings'));
          }
        } else if (data.field_errors) {
          // Direct field errors format
          setFieldErrors(data.field_errors);
          setError(data.message || data.error || t('dashboard.settings.voiceratelimitingsettings.validationErrorHighlight'));
        } else {
          // Simple error message
          setError(data.message || data.detail || t('dashboard.settings.voiceratelimitingsettings.errorUpdateSettings'));
        }
        return;
      }

      setLastSavedVoiceRateLimit(voiceRateLimitSettings);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
      await fetchWorkspaceDetails(currentWorkspace.workspace_id, true); // Force refresh
    }
  };

  const handleResetVoiceRateLimit = () => {
    setVoiceRateLimitSettings(lastSavedVoiceRateLimit);
    setFieldErrors({});
  };

  // Validation functions
  const validateVoiceRateLimitField = (field, value) => {
    const boundaries = {
      turnsPerMinute: { min: 1, max: 20, labelKey: "fieldLabelTurnsPerMinute" },
      turnsPerHour: { min: 5, max: 100, labelKey: "fieldLabelTurnsPerHour" },
      turnsPerDay: { min: 20, max: 500, labelKey: "fieldLabelTurnsPerDay" },
      maxConversationDuration: { min: 5, max: 120, labelKey: "fieldLabelMaxConversationDuration" },
      burstLimit: { min: 1, max: 10, labelKey: "fieldLabelBurstLimit" },
      blockDuration: { min: 1, max: 1440, labelKey: "fieldLabelBlockDuration" },
      maxConcurrentSessions: { min: 1, max: 5, labelKey: "fieldLabelMaxConcurrentSessions" }
    };

    const boundary = boundaries[field];
    if (!boundary) return null;

    const label = t(`dashboard.settings.voiceratelimitingsettings.${boundary.labelKey}`);

    if (value < boundary.min) {
      return t('dashboard.settings.voiceratelimitingsettings.validationMinValue', { label, min: boundary.min });
    }
    if (value > boundary.max) {
      return t('dashboard.settings.voiceratelimitingsettings.validationMaxValue', { label, max: boundary.max });
    }
    return null;
  };

  const validateLogicalRelationships = (settings) => {
    const errors = {};

    // Burst limit should not exceed turns per minute
    if (settings.burstLimit > settings.turnsPerMinute) {
      errors.burstLimit = t('dashboard.settings.voiceratelimitingsettings.validationBurstExceedsMinute', {
        burst: settings.burstLimit,
        minute: settings.turnsPerMinute
      });
    }

    // Turns per hour should be at least equal to turns per minute
    if (settings.turnsPerHour < settings.turnsPerMinute) {
      errors.turnsPerHour = t('dashboard.settings.voiceratelimitingsettings.validationHourBelowMinute', {
        hour: settings.turnsPerHour,
        minute: settings.turnsPerMinute
      });
    }

    // Turns per day should be at least equal to turns per hour
    if (settings.turnsPerDay < settings.turnsPerHour) {
      errors.turnsPerDay = t('dashboard.settings.voiceratelimitingsettings.validationDayBelowHour', {
        day: settings.turnsPerDay,
        hour: settings.turnsPerHour
      });
    }

    return errors;
  };

  const validateAllVoiceRateLimitSettings = (settings) => {
    const errors = {};

    // Validate individual field boundaries
    Object.keys(settings).forEach(field => {
      const error = validateVoiceRateLimitField(field, settings[field]);
      if (error) {
        errors[field] = error;
      }
    });

    // Only validate logical relationships if no boundary errors
    if (Object.keys(errors).length === 0) {
      const logicalErrors = validateLogicalRelationships(settings);
      Object.assign(errors, logicalErrors);
    }

    return errors;
  };

  // Clear field errors when user starts typing and validate in real-time
  const handleVoiceRateLimitChange = (field, value) => {
    const newSettings = { ...voiceRateLimitSettings, [field]: value };
    setVoiceRateLimitSettings(newSettings);

    // Start with empty errors
    const newFieldErrors = {};

    // Validate ALL fields for boundary errors with new settings
    Object.keys(newSettings).forEach(key => {
      const error = validateVoiceRateLimitField(key, newSettings[key]);
      if (error) {
        newFieldErrors[key] = error;
      }
    });

    // If no boundary errors, validate logical relationships
    if (Object.keys(newFieldErrors).length === 0) {
      const logicalErrors = validateLogicalRelationships(newSettings);
      Object.assign(newFieldErrors, logicalErrors);
    }

    setFieldErrors(newFieldErrors);
  };

  // Create PageHeader component to be used in both skeleton and main render
  const pageHeaderComponent = (
    <PageHeader
      icon={<FiMic />}
      title={t('dashboard.settings.voiceratelimitingsettings.pageTitle')}
      description={t('dashboard.settings.voiceratelimitingsettings.pageDescription')}
      hideDescriptionOnMobile={false}
      cancelButton={settingsPermissions.canModify ? {
        children: t('dashboard.settings.voiceratelimitingsettings.resetButton'),
        onClick: handleResetVoiceRateLimit,
        disabled: isSaving || !isVoiceRateLimitChanged(),
        variant: "outline-secondary"
      } : null}
      saveButton={settingsPermissions.canModify ? {
        children: isSaving ? (
          <>
            <Spinner
              as="span"
              animation="border"
              size="sm"
              role="status"
              aria-hidden="true"
              className="me-2"
            />
            {t('dashboard.settings.voiceratelimitingsettings.savingText')}
          </>
        ) : hasValidationErrors() ? (
          t('dashboard.settings.voiceratelimitingsettings.fixErrorsButton')
        ) : isVoiceRateLimitChanged() ? (
          t('dashboard.settings.voiceratelimitingsettings.saveButton')
        ) : (
          t('dashboard.settings.voiceratelimitingsettings.savedButton')
        ),
        onClick: handleSaveVoiceRateLimit,
        disabled: isSaving || !isVoiceRateLimitChanged() || hasValidationErrors(),
        variant: "primary"
      } : null}
    />
  );

  if (loading) {
    return <RateLimitingSettingsSkeleton pageHeader={pageHeaderComponent} />;
  }

  return (
    <div className="agent-settings-container">
      {pageHeaderComponent}

      <div className="settings-content">
        {/* Display save/fetch errors */}
        {error && (
          <Alert variant="danger" className="settings-alert">
            {error}
          </Alert>
        )}

        <div className="settings-form-container">
          <Form>

            <div className="settings-form-grid">
              <div className="settings-form-group">
                <Form.Label className="settings-label">{t('dashboard.settings.voiceratelimitingsettings.turnsPerMinuteLabel')}</Form.Label>
                <div className={`settings-input-wrapper ${!settingsPermissions.canModifyRateLimit ? 'permission-restricted' : ''}`}>
                  <InputGroup className="settings-input-group">
                    <Form.Control
                      type="number"
                      min="1"
                      max="20"
                      value={voiceRateLimitSettings.turnsPerMinute}
                      onChange={(e) => handleVoiceRateLimitChange('turnsPerMinute', parseInt(e.target.value))}
                      className={`settings-input ${fieldErrors.turnsPerMinute ? 'is-invalid' : ''}`}
                      disabled={!settingsPermissions.canModifyRateLimit}
                      readOnly={!settingsPermissions.canModifyRateLimit}
                    />
                    <InputGroup.Text className="settings-input-addon">{t('dashboard.settings.voiceratelimitingsettings.turnsPerMinuteUnit')}</InputGroup.Text>
                  </InputGroup>
                </div>
                {fieldErrors.turnsPerMinute && (
                  <div className="settings-error-text">
                    {fieldErrors.turnsPerMinute}
                  </div>
                )}
                <Form.Text className="settings-help-text">
                  {t('dashboard.settings.voiceratelimitingsettings.turnsPerMinuteHelp')}
                </Form.Text>
              </div>

              <div className="settings-form-group">
                <Form.Label className="settings-label">{t('dashboard.settings.voiceratelimitingsettings.burstLimitLabel')}</Form.Label>
                <div className={`settings-input-wrapper ${!settingsPermissions.canModifyRateLimit ? 'permission-restricted' : ''}`}>
                  <InputGroup className="settings-input-group">
                    <Form.Control
                      type="number"
                      min="1"
                      max="10"
                      value={voiceRateLimitSettings.burstLimit}
                      onChange={(e) => handleVoiceRateLimitChange('burstLimit', parseInt(e.target.value))}
                      className={`settings-input ${fieldErrors.burstLimit ? 'is-invalid' : ''}`}
                      disabled={!settingsPermissions.canModifyRateLimit}
                      readOnly={!settingsPermissions.canModifyRateLimit}
                    />
                    <InputGroup.Text className="settings-input-addon">{t('dashboard.settings.voiceratelimitingsettings.burstLimitUnit')}</InputGroup.Text>
                  </InputGroup>
                </div>
                {fieldErrors.burstLimit && (
                  <div className="settings-error-text">
                    {fieldErrors.burstLimit}
                  </div>
                )}
                <Form.Text className="settings-help-text">
                  {t('dashboard.settings.voiceratelimitingsettings.burstLimitHelp')}
                </Form.Text>
              </div>

              <div className="settings-form-group">
                <Form.Label className="settings-label">{t('dashboard.settings.voiceratelimitingsettings.turnsPerHourLabel')}</Form.Label>
                <div className={`settings-input-wrapper ${!settingsPermissions.canModifyRateLimit ? 'permission-restricted' : ''}`}>
                  <InputGroup className="settings-input-group">
                    <Form.Control
                      type="number"
                      min="5"
                      max="100"
                      value={voiceRateLimitSettings.turnsPerHour}
                      onChange={(e) => handleVoiceRateLimitChange('turnsPerHour', parseInt(e.target.value))}
                      className={`settings-input ${fieldErrors.turnsPerHour ? 'is-invalid' : ''}`}
                      disabled={!settingsPermissions.canModifyRateLimit}
                      readOnly={!settingsPermissions.canModifyRateLimit}
                    />
                    <InputGroup.Text className="settings-input-addon">{t('dashboard.settings.voiceratelimitingsettings.turnsPerHourUnit')}</InputGroup.Text>
                  </InputGroup>
                </div>
                {fieldErrors.turnsPerHour && (
                  <div className="settings-error-text">
                    {fieldErrors.turnsPerHour}
                  </div>
                )}
              </div>

              <div className="settings-form-group">
                <Form.Label className="settings-label">{t('dashboard.settings.voiceratelimitingsettings.turnsPerDayLabel')}</Form.Label>
                <div className={`settings-input-wrapper ${!settingsPermissions.canModifyRateLimit ? 'permission-restricted' : ''}`}>
                  <InputGroup className="settings-input-group">
                    <Form.Control
                      type="number"
                      min="20"
                      max="500"
                      value={voiceRateLimitSettings.turnsPerDay}
                      onChange={(e) => handleVoiceRateLimitChange('turnsPerDay', parseInt(e.target.value))}
                      className={`settings-input ${fieldErrors.turnsPerDay ? 'is-invalid' : ''}`}
                      disabled={!settingsPermissions.canModifyRateLimit}
                      readOnly={!settingsPermissions.canModifyRateLimit}
                    />
                    <InputGroup.Text className="settings-input-addon">{t('dashboard.settings.voiceratelimitingsettings.turnsPerDayUnit')}</InputGroup.Text>
                  </InputGroup>
                </div>
                {fieldErrors.turnsPerDay && (
                  <div className="settings-error-text">
                    {fieldErrors.turnsPerDay}
                  </div>
                )}
              </div>

              <div className="settings-form-group">
                <Form.Label className="settings-label">{t('dashboard.settings.voiceratelimitingsettings.maxConversationDurationLabel')}</Form.Label>
                <div className={`settings-input-wrapper ${!settingsPermissions.canModifyRateLimit ? 'permission-restricted' : ''}`}>
                  <InputGroup className="settings-input-group">
                    <Form.Control
                      type="number"
                      min="5"
                      max="120"
                      value={voiceRateLimitSettings.maxConversationDuration}
                      onChange={(e) => handleVoiceRateLimitChange('maxConversationDuration', parseInt(e.target.value))}
                      className={`settings-input ${fieldErrors.maxConversationDuration ? 'is-invalid' : ''}`}
                      disabled={!settingsPermissions.canModifyRateLimit}
                      readOnly={!settingsPermissions.canModifyRateLimit}
                    />
                    <InputGroup.Text className="settings-input-addon">{t('dashboard.settings.voiceratelimitingsettings.maxConversationDurationUnit')}</InputGroup.Text>
                  </InputGroup>
                </div>
                {fieldErrors.maxConversationDuration && (
                  <div className="settings-error-text">
                    {fieldErrors.maxConversationDuration}
                  </div>
                )}
                <Form.Text className="settings-help-text">
                  {t('dashboard.settings.voiceratelimitingsettings.maxConversationDurationHelp')}
                </Form.Text>
              </div>

              <div className="settings-form-group">
                <Form.Label className="settings-label">{t('dashboard.settings.voiceratelimitingsettings.maxConcurrentSessionsLabel')}</Form.Label>
                <div className={`settings-input-wrapper ${!settingsPermissions.canModifyRateLimit ? 'permission-restricted' : ''}`}>
                  <InputGroup className="settings-input-group">
                    <Form.Control
                      type="number"
                      min="1"
                      max="5"
                      value={voiceRateLimitSettings.maxConcurrentSessions}
                      onChange={(e) => handleVoiceRateLimitChange('maxConcurrentSessions', parseInt(e.target.value))}
                      className={`settings-input ${fieldErrors.maxConcurrentSessions ? 'is-invalid' : ''}`}
                      disabled={!settingsPermissions.canModifyRateLimit}
                      readOnly={!settingsPermissions.canModifyRateLimit}
                    />
                    <InputGroup.Text className="settings-input-addon">{t('dashboard.settings.voiceratelimitingsettings.maxConcurrentSessionsUnit')}</InputGroup.Text>
                  </InputGroup>
                </div>
                {fieldErrors.maxConcurrentSessions && (
                  <div className="settings-error-text">
                    {fieldErrors.maxConcurrentSessions}
                  </div>
                )}
                <Form.Text className="settings-help-text">
                  {t('dashboard.settings.voiceratelimitingsettings.maxConcurrentSessionsHelp')}
                </Form.Text>
              </div>

              <div className="settings-form-group">
                <Form.Label className="settings-label">{t('dashboard.settings.voiceratelimitingsettings.blockDurationLabel')}</Form.Label>
                <div className={`settings-input-wrapper ${!settingsPermissions.canModifyRateLimit ? 'permission-restricted' : ''}`}>
                  <InputGroup className="settings-input-group">
                    <Form.Control
                      type="number"
                      min="1"
                      max="1440"
                      value={voiceRateLimitSettings.blockDuration}
                      onChange={(e) => handleVoiceRateLimitChange('blockDuration', parseInt(e.target.value))}
                      className={`settings-input ${fieldErrors.blockDuration ? 'is-invalid' : ''}`}
                      disabled={!settingsPermissions.canModifyRateLimit}
                      readOnly={!settingsPermissions.canModifyRateLimit}
                    />
                    <InputGroup.Text className="settings-input-addon">{t('dashboard.settings.voiceratelimitingsettings.maxConversationDurationUnit')}</InputGroup.Text>
                  </InputGroup>
                </div>
                {fieldErrors.blockDuration && (
                  <div className="settings-error-text">
                    {fieldErrors.blockDuration}
                  </div>
                )}
                <Form.Text className="settings-help-text">
                  {t('dashboard.settings.voiceratelimitingsettings.blockDurationHelp')}
                </Form.Text>
              </div>
            </div>

          </Form>

        </div>

        {showSuccess && (
          <Alert variant="success" className="settings-success-alert">
            {t('dashboard.settings.voiceratelimitingsettings.successSaved')}
          </Alert>
        )}
      </div>
    </div>
  );
};

export default VoiceRateLimitingSettings;
